// The block plan.
//
// This module produces DATA only -- no Three.js. build.js turns it into meshes, collision.js's
// World gets fed from the same records. One source of truth: a wall and the thing that stops you
// walking through the wall are the same record, so they cannot drift apart.
//
// Layout, looking down (+Z is north, +X is east):
//
//        x=-28        x=-8   x=0   x=+8         x=+28
//   z=+42  +------------+     |     +-------------+
//          |  W7        |  s  |  s  |  E7         |
//          |            |  i  |  i  |             |
//   z=+15  +------------+  d  |  d  +=============+  <- ALLEY EAST (z 13..15)
//          |  W6/W5     |  e  |  e  |  E5/E6      |
//   z=-9   +============+  w  |  w  +-------------+  <- ALLEY WEST (z -11.4..-9)
//          |  W3        |  a  |  a  |  E3         |
//   z=-18  |            |  l  |  l  +#############+  <- ARCADE (z -22.5..-18), covered
//          |  W2/W1     |  k  |  k  |  E1/E2      |
//   z=-42  +------------+     |     +-------------+
//
// The steel switchback stair stands in ALLEY WEST and lands on the elevated WALKWAY, which is
// cantilevered off the west faces over the sidewalk from z=-20 to z=+12 at y=6.4.

import { rngFor } from '../rng.js';
import { WORLD, WALKWAY_Y, ARCADE_Y } from '../config.js';

export const ALLEY_W = { z0: -11.4, z1: -9.0, x0: -28, x1: -WORLD.FACE };
export const ALLEY_E = { z0: 13.0, z1: 15.0, x0: WORLD.FACE, x1: 26 };
/**
 * The covered arcade. z0..z1 is the FULL BAND the arcade occupies in the block -- concourse plus a
 * BAY of shop depth on each side. The walkable concourse is z0+BAY .. z1-BAY.
 *
 * Round 5: concourse widened 4.5 -> 6.5 m clear (a critic measured the built result narrower than
 * it was tall and called it a corridor, not an arcade), and the band was extended to carry the shop
 * bays. Previously the shop interiors were carved out of the flanking parcels' footprints, which
 * means they were built INSIDE solid building mass and could never be seen -- the same burial bug
 * as the windows. The parcel splits reference ARCADE.z0/z1, so the neighbours resize automatically.
 */
export const ARCADE = {
  z0: -25.5, z1: -15.2, bay: 1.9,
  x0: WORLD.FACE, x1: 24.5, ceil: ARCADE_Y,
  get cz0() { return this.z0 + this.bay; },   // concourse edges
  get cz1() { return this.z1 - this.bay; },
};
export const WALKWAY = { x0: -WORLD.FACE, x1: -6.05, z0: -20.0, z1: 12.0, y: WALKWAY_Y };

// Parcel edges along Z for each block. Gaps are the alley / arcade cuts above.
const WEST_SPLITS = [-42, -31, -21.5, ALLEY_W.z0, ALLEY_W.z1, -0.5, 10.0, 21.0, 42];
const EAST_SPLITS = [-42, -31, ARCADE.z0, ARCADE.z1, -8.5, 2.5, ALLEY_E.z0, ALLEY_E.z1, 26.5, 42];

// Which of those spans are actual buildings (the cut spans are not).
const isCut = (z0, z1) =>
  (z0 === ALLEY_W.z0 && z1 === ALLEY_W.z1) ||
  (z0 === ALLEY_E.z0 && z1 === ALLEY_E.z1) ||
  (z0 === ARCADE.z0 && z1 === ARCADE.z1);

/**
 * A building parcel. `faceX` is the street-facing plane; buildings jog in and out by up to 0.5 m,
 * which is what stops a block of flat-fronted boxes reading as a corridor of cardboard.
 */
function makeParcel(side, i, z0, z1) {
  const key = `parcel.${side}.${i}`;
  const r = rngFor(key);
  const sign = side === 'west' ? -1 : 1;

  // Heights. A real dense block is NOT uniform and is NOT randomly uniform either -- it clusters,
  // because buildings went up in eras. Bias toward two bands with occasional outliers.
  const band = r.weighted(['low', 'mid', 'tall'], [0.34, 0.46, 0.20]);
  const h = band === 'low' ? r.range(10.5, 14.5)
    : band === 'mid' ? r.range(16.0, 21.0)
      : r.range(24.0, 31.0);

  const setback = r.range(-0.18, 0.52);          // + = pulled back from the street
  const faceX = sign * (WORLD.FACE + setback);
  const backX = sign * (WORLD.FACE + WORLD.BLOCK_DEPTH);

  // Ground floor: shopfront recess + bulkhead. Real streets read as a continuous glazed band at
  // eye level under a heavier mass -- that contrast is most of what says "city".
  //
  // Round 5: retail ground storeys are 4.0-4.6 m clear, NOT the 2.9-3.4 m of the flats above. This
  // was 3.1-3.9, i.e. the same as an upper floor, and a critic named it twice as the reason the
  // blocks read as stacked identical slabs rather than buildings.
  const shopH = r.range(4.0, 4.6);
  const shopRecess = r.range(0.35, 0.95);

  // Floor rhythm above the shopfront.
  const floorH = r.range(2.85, 3.45);
  const floors = Math.max(2, Math.floor((h - shopH) / floorH));

  return {
    key, side, i, z0, z1, sign,
    depth: z1 - z0,
    faceX, backX, h, band,
    shopH, shopRecess, floorH, floors,
    // Facade dressing decided once, here, so build.js is pure construction.
    windowCols: Math.max(2, Math.round((z1 - z0) / r.range(2.0, 2.9))),
    fireEscape: r.chance(side === 'west' ? 0.42 : 0.34) && (z1 - z0) > 8,
    awning: r.chance(0.62),
    awningDrop: r.range(0.9, 1.6),
    ac: r.int(2, 7),
    downpipes: r.int(1, 3),
    roofClutter: r.int(2, 5),
    // Sign armatures: brackets only. The signs themselves are System 4's job.
    brackets: r.int(1, 4),
    parapet: r.range(0.5, 1.4),
    // Vertical banding -- a change of material partway up. Reads as an addition or a re-clad.
    bandY: r.chance(0.45) ? r.range(0.45, 0.72) * h : null,
  };
}

export function buildPlan() {
  const parcels = [];

  for (let i = 0; i < WEST_SPLITS.length - 1; i++) {
    const z0 = WEST_SPLITS[i], z1 = WEST_SPLITS[i + 1];
    if (isCut(z0, z1)) continue;
    parcels.push(makeParcel('west', i, z0, z1));
  }
  for (let i = 0; i < EAST_SPLITS.length - 1; i++) {
    const z0 = EAST_SPLITS[i], z1 = EAST_SPLITS[i + 1];
    if (isCut(z0, z1)) continue;
    parcels.push(makeParcel('east', i, z0, z1));
  }

  return {
    parcels,
    alleyW: ALLEY_W,
    alleyE: ALLEY_E,
    arcade: ARCADE,
    walkway: WALKWAY,
    stair: makeStair(),
    props: makeProps(),
    cables: makeCables(),
  };
}

/**
 * Exterior steel switchback stair, standing in the west alley. Rises alley floor -> 3.15 -> 6.4,
 * then a landing that runs east out of the alley mouth onto the walkway.
 *
 * Real service stairs are ~30-35 degrees. Getting this wrong is one of the loudest scale tells in a
 * synthetic scene: too shallow and the building reads short, too steep and it reads like a ladder.
 */
function makeStair() {
  const w = 1.05;                 // flight width -- service stair, one person
  const zc = (ALLEY_W.z0 + ALLEY_W.z1) / 2;
  const rise1 = 3.15, rise2 = WALKWAY_Y - rise1;
  const run1 = 4.6, run2 = 4.6;   // ~34 deg with rise1 over run1

  return {
    width: w,
    zLo: zc - w / 2, zHi: zc + w / 2,
    // Flight A: travels WEST (-x), climbing.
    a: { x0: -14.8, x1: -10.2, yLo: 0, yHi: rise1, axis: 'x', dir: -1, run: run1 },
    // Mid landing at the west end.
    landA: { x0: -16.3, x1: -14.8, y: rise1 },
    // Flight B: travels EAST (+x), climbing.
    b: { x0: -16.3, x1: -11.7, yLo: rise1, yHi: rise1 + rise2, axis: 'x', dir: 1, run: run2 },
    // Top landing, then the connector east to the walkway.
    landB: { x0: -11.7, x1: -WORLD.FACE, y: WALKWAY_Y },
    railH: 1.06,
  };
}

/**
 * Street and alley clutter. Density over size -- the brief's words. A believable street is mostly
 * things people put there and then stopped thinking about.
 */
function makeProps() {
  const out = [];
  const push = (o) => out.push(o);

  // --- Sidewalk furniture along both sides ---------------------------------
  for (const side of ['west', 'east']) {
    const sign = side === 'west' ? -1 : 1;
    const r = rngFor(`street.furniture.${side}`);
    // Round 5: pitch tightened 3.4-7.5 -> 2.0-4.6, and wall-hugging raised 0.55 -> 0.80. Real
    // pavements scour clean down the walking line and accumulate against the building face; the
    // previous distribution was both too sparse and too even, and put objects mid-footway.
    for (let z = WORLD.STREET_Z0 + 3; z < WORLD.STREET_Z1 - 3; z += r.range(2.0, 4.6)) {
      const kind = r.weighted(
        ['bollard', 'utilbox', 'crates', 'bin', 'pole', 'hydrant', 'stack', 'aboard', 'bikerack', 'signpost', 'sacks'],
        [0.13, 0.10, 0.11, 0.12, 0.09, 0.05, 0.10, 0.09, 0.07, 0.08, 0.06]
      );
      // Against the kerb or against the wall -- never floating in the middle of the walk.
      const againstWall = r.chance(0.80);
      const x = againstWall
        ? sign * (WORLD.FACE - r.range(0.35, 0.95))
        : sign * (WORLD.ROAD_HALF + r.range(0.35, 0.8));
      push({ kind, x, z: z + r.jitter(0.6), rot: r.range(-0.35, 0.35), key: `f.${side}.${z.toFixed(1)}`, seed: r.f() });
    }
  }

  // --- Arcade: was completely empty over ~35 m ------------------------------
  // Deliberately ASYMMETRIC -- everything down one side. A critic scored this frame 1/10 twice,
  // partly because a covered arcade with mirror symmetry and nothing on the floor reads as an
  // office corridor rather than a street with a roof on it.
  {
    const r = rngFor('arcade.pop');
    const zN = ARCADE.cz0 + 0.75, zS = ARCADE.cz1 - 0.75;
    for (let x = ARCADE.x0 + 2.0; x < ARCADE.x1 - 1.5; x += r.range(1.5, 3.2)) {
      const kind = r.weighted(
        ['vending', 'aboard', 'bin', 'crates', 'bikerack', 'stack'],
        [0.22, 0.20, 0.14, 0.16, 0.16, 0.12]
      );
      // 78% on the north side -- clustered, not distributed.
      const z = r.chance(0.78) ? zN + r.jitter(0.25) : zS + r.jitter(0.25);
      push({ kind, x: x + r.jitter(0.4), z, rot: r.range(-0.3, 0.3), arcade: true, key: `ar.${x.toFixed(1)}`, seed: r.f() });
    }
  }

  // --- Elevated walkway: also completely empty -----------------------------
  {
    const r = rngFor('walkway.pop');
    for (let z = WALKWAY.z0 + 2.5; z < WALKWAY.z1 - 1.5; z += r.range(3.0, 6.5)) {
      const kind = r.weighted(['walklamp', 'bin', 'crates'], [0.55, 0.25, 0.20]);
      push({
        kind, x: WALKWAY.x0 + r.range(0.35, 0.75), z, rot: r.range(-0.2, 0.2),
        walkway: true, key: `wk.${z.toFixed(1)}`, seed: r.f(),
      });
    }
  }

  // --- Alley clutter: much denser, and it blocks the walk -------------------
  for (const [name, a] of [['w', ALLEY_W], ['e', ALLEY_E]]) {
    const r = rngFor(`alley.clutter.${name}`);
    const zc = (a.z0 + a.z1) / 2;
    // Round 5: pitch 1.6-3.4 -> 0.8-1.9. A real service alley carries 30-60 objects over 20 m; this
    // was producing about eight, which reads as a corridor rather than a place things get dumped.
    for (let x = a.x0 + 1.5; x < a.x1 - 1.5; x += r.range(0.8, 1.9)) {
      const kind = r.weighted(
        ['dumpster', 'crates', 'bin', 'condenser', 'pallet', 'drum', 'stack', 'sacks', 'gasbottle'],
        [0.12, 0.17, 0.13, 0.10, 0.11, 0.09, 0.10, 0.10, 0.08]
      );
      const side = r.chance(0.5) ? -1 : 1;
      // Shoved against a wall, not standing in the middle of the alley.
      const z = zc + side * r.range(0.62, 0.95);
      push({ kind, x: x + r.jitter(0.4), z, rot: r.range(-0.5, 0.5), alley: name, key: `a.${name}.${x.toFixed(1)}`, seed: r.f() });
    }
  }

  // --- Parked vehicles ------------------------------------------------------
  // A city street at night with zero vehicles parked along 80 m of kerb is disqualifying on its
  // own, and a car is also the most legible scale datum available short of a person. (People are
  // System 7's job -- procedural humanoid bodies -- so they are NOT built here.)
  //
  // Parked nose-to-tail with gaps, never perfectly spaced, offset 0.4 m off the kerb face.
  for (const side of ['west', 'east']) {
    const sign = side === 'west' ? -1 : 1;
    const r = rngFor(`street.parked.${side}`);
    let z = WORLD.STREET_Z0 + r.range(4, 9);
    while (z < WORLD.STREET_Z1 - 8) {
      if (r.chance(0.22)) { z += r.range(6, 14); continue; }  // a gap in the parked run
      const kind = r.weighted(['car', 'car', 'van', 'scooter'], [0.34, 0.34, 0.18, 0.14]);
      const len = kind === 'van' ? 5.4 : kind === 'scooter' ? 1.9 : 4.4;
      push({
        kind, x: sign * (WORLD.ROAD_HALF - 0.4 - (kind === 'scooter' ? 0.5 : 0.9)),
        z: z + len / 2, rot: sign > 0 ? 0 : Math.PI, len,
        key: `veh.${side}.${z.toFixed(1)}`, seed: r.f(),
      });
      z += len + r.range(0.6, 1.8);
    }
  }

  // --- Roadway: drains, manholes, the odd barrier --------------------------
  const rr = rngFor('road.fixtures');
  for (let z = WORLD.STREET_Z0 + 5; z < WORLD.STREET_Z1 - 5; z += rr.range(7, 13)) {
    push({ kind: 'manhole', x: rr.range(-3.2, 3.2), z, rot: rr.range(0, 3.14), key: `mh.${z.toFixed(0)}`, seed: rr.f() });
  }
  for (const sign of [-1, 1]) {
    const r2 = rngFor(`road.drains.${sign}`);
    for (let z = WORLD.STREET_Z0 + 4; z < WORLD.STREET_Z1 - 4; z += r2.range(9, 15)) {
      push({ kind: 'drain', x: sign * (WORLD.ROAD_HALF - 0.34), z, rot: 0, key: `dr.${sign}.${z.toFixed(0)}`, seed: r2.f() });
    }
  }

  return out;
}

/**
 * Overhead cable runs. This is the highest-leverage single element for reading as a real dense
 * street: the sky is never empty above one of these places. Catenary sag, not straight lines.
 */
function makeCables() {
  const out = [];
  const r = rngFor('cables.street');
  for (let z = WORLD.STREET_Z0 + 6; z < WORLD.STREET_Z1 - 6; z += r.range(4.5, 9.0)) {
    const n = r.int(2, 5);
    for (let i = 0; i < n; i++) {
      const y = r.range(6.2, 13.5);
      out.push({
        a: [-WORLD.FACE + r.jitter(0.25), y, z + r.jitter(0.9)],
        b: [WORLD.FACE + r.jitter(0.25), y + r.jitter(1.4), z + r.jitter(1.6)],
        sag: r.range(0.55, 1.9),
        rad: r.range(0.012, 0.032),
        key: `cab.${z.toFixed(0)}.${i}`,
      });
    }
  }
  // A few running ALONG the street too, wall to wall on the same side.
  for (const side of ['west', 'east']) {
    const sign = side === 'west' ? -1 : 1;
    const r2 = rngFor(`cables.along.${side}`);
    for (let z = WORLD.STREET_Z0 + 8; z < WORLD.STREET_Z1 - 10; z += r2.range(9, 16)) {
      const y = r2.range(5.6, 9.5);
      const len = r2.range(7, 14);
      out.push({
        a: [sign * (WORLD.FACE - 0.15), y, z],
        b: [sign * (WORLD.FACE - 0.15) + r2.jitter(0.5), y + r2.jitter(0.8), z + len],
        sag: r2.range(0.35, 1.1),
        rad: r2.range(0.010, 0.022),
        key: `cabl.${side}.${z.toFixed(0)}`,
      });
    }
  }
  return out;
}
